import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  House, ListChecks, ChatsCircle, Receipt, Gear,
  SignOut, BookOpenText, Buildings, ClockCounterClockwise, CalendarBlank, Question, Folder, ShieldCheck, CurrencyDollar, HandWaving, X,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";
import { useNotifications } from "../context/NotificationsContext";
import CompanySwitcher from "./CompanySwitcher";

const QUICK_START_DISMISSED_KEY = "quick-start-dismissed-v1";

const navItems = [
  { to: "/recurring", label: "Reoccurring Monthly", icon: ListChecks, key: "monthly" },
  { to: "/payment-advices", label: "Remittances", icon: Receipt, key: "remit" },
  { to: "/questions", label: "Inquiries", icon: ChatsCircle, key: "inquiries" },
  { to: "/close", label: "Month-end Close", icon: ShieldCheck, key: "close" },
  { to: "/files", label: "Files", icon: Folder, key: "files" },
  { to: "/activity", label: "Activity Log", icon: ClockCounterClockwise, key: "activity" },
  { to: "/history", label: "Year View", icon: CalendarBlank, key: "history" },
  { to: "/help", label: "Help", icon: Question, key: "help" },
  { to: "/settings", label: "Settings", icon: Gear, key: "settings" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { activeCompany } = useCompany();
  const { unresolvedInquiries = 0 } = useNotifications() || {};
  const nav = useNavigate();

  // Quick Start tile is dismissable per-browser. Once dismissed, stays gone.
  // Hidden default for accounts older than 30 days (assumed past onboarding).
  const [quickStartDismissed, setQuickStartDismissed] = useState(() => {
    try { return localStorage.getItem(QUICK_START_DISMISSED_KEY) === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    if (!user) return;
    // Auto-dismiss for users whose account is 30+ days old — they've
    // clearly graduated from onboarding and don't need this taking up space.
    const created = user.created_at ? new Date(user.created_at).getTime() : null;
    if (created && Date.now() - created > 30 * 24 * 60 * 60 * 1000) {
      setQuickStartDismissed(true);
    }
  }, [user]);
  const dismissQuickStart = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    try { localStorage.setItem(QUICK_START_DISMISSED_KEY, "1"); } catch {}
    setQuickStartDismissed(true);
  };

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <aside
      data-testid="sidebar"
      className="hidden lg:flex w-72 flex-col bg-white border-r border-[#E5E5E5] sticky top-0 h-screen"
    >
      {/* Brand */}
      <div className="px-6 py-6 border-b border-[#E5E5E5] flex items-center gap-3">
        <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center flex-shrink-0">
          <BookOpenText size={18} weight="duotone" className="text-white" />
        </div>
        <div className="min-w-0">
          <div className="font-heading text-sm font-semibold tracking-tight leading-tight truncate">
            Bookkeeping
          </div>
          <div className="text-[11px] text-[#525252] tracking-wider uppercase">Portal</div>
        </div>
      </div>

      {/* Company Switcher */}
      <div className="p-4 border-b border-[#E5E5E5]">
        <div className="label-tag mb-2 flex items-center gap-1.5">
          <Buildings size={12} weight="duotone" /> Active entity
        </div>
        <CompanySwitcher />
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-1">
        {/* Pinned "Quick start" — dismissable. Sits above Overview, styled in
            a slightly transparent green so new users see it immediately. Once
            dismissed (× button) or after 30 days of account age, it disappears. */}
        {!quickStartDismissed && (
          <NavLink
            to="/recurring"
            data-testid="nav-quick-start-pinned"
            className={({ isActive }) =>
              `relative flex items-center gap-3 px-3 py-2.5 text-sm transition-all mb-2 border ${
                isActive
                  ? "bg-[#4A9D3C] text-white border-[#4A9D3C]"
                  : "bg-[#4A9D3C]/15 text-[#2D5A1B] border-[#4A9D3C]/40 hover:bg-[#4A9D3C]/25 hover:border-[#4A9D3C]/70"
              }`
            }
          >
            <HandWaving size={18} weight="duotone" />
            <span className="font-semibold flex-1">Quick start</span>
            <button
              onClick={dismissQuickStart}
              data-testid="nav-quick-start-dismiss"
              title="Hide this from the sidebar"
              aria-label="Dismiss Quick start"
              className="opacity-70 hover:opacity-100 hover:bg-black/10 p-0.5 transition-all"
            >
              <X size={12} weight="bold" />
            </button>
          </NavLink>
        )}
        {navItems.map(({ to, label, icon: Icon, end, key, inactive }) => {
          const isInquiries = key === "inquiries";
          const showRed = isInquiries && unresolvedInquiries > 0;
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-all group ${
                  isActive
                    ? "bg-[#0A0A0A] text-white"
                    : showRed
                      ? "text-[#DC2626] hover:bg-[#DC2626]/10"
                      : inactive
                        ? "text-[#A3A3A3] hover:bg-[#F5F5F5] hover:text-[#737373]"
                        : "text-[#0A0A0A] hover:bg-[#F5F5F5]"
                }`
              }
            >
              <Icon size={18} weight={showRed ? "fill" : "duotone"} />
              <span className={`font-medium flex-1 ${inactive ? "italic" : ""}`}>{label}</span>
              {inactive && (
                <span
                  className="text-[9px] tracking-wider uppercase bg-[#E5E5E5] text-[#737373] px-1.5 py-0.5 font-semibold"
                  title="This feature is not active yet"
                >
                  Soon
                </span>
              )}
              {showRed && (
                <span
                  data-testid="sidebar-inquiries-badge"
                  className="min-w-[20px] h-[20px] px-1.5 bg-[#DC2626] text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                >
                  {unresolvedInquiries > 9 ? "9+" : unresolvedInquiries}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Active entity logo */}
      {activeCompany?.name && (
        <div className="px-4 pb-3" data-testid="sidebar-entity-logo">
          {activeCompany.name === "Todd's Nursery and Landscaping" && (
            <div className="bg-white border border-[#E5E5E5] rounded-md p-3 flex items-center justify-center">
              <img
                src="/todds-nursery-logo.png"
                alt="Todd's Nursery and Landscaping"
                className="w-full h-auto max-h-16 object-contain"
              />
            </div>
          )}
          {activeCompany.name === "North Star Grounds Care" && (
            <div className="bg-black rounded-md p-3 flex items-center justify-center">
              <img
                src="/north-star-logo.png"
                alt="North Star Grounds Care"
                className="w-full h-auto max-h-14 object-contain"
              />
            </div>
          )}
        </div>
      )}

      {/* User footer */}
      <div className="p-4 border-t border-[#E5E5E5]">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate" data-testid="user-name">
              {user?.name}
            </div>
            <div className="label-tag text-[10px]">{user?.role}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="p-2 hover:bg-[#F5F5F5] transition-all"
            title="Sign out"
          >
            <SignOut size={18} weight="duotone" />
          </button>
        </div>
      </div>
    </aside>
  );
}
